import { action, computed, makeObservable } from 'mobx';

import { AlertsStore } from '@/configs/store/AlertsStore';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { LocalStore } from '@/models/LocalStore';
import { ValueModel } from '@/models/ValueModel';
import { trackerApi } from '@/tracker/api/trackerApi';
import { QueueMember } from '@/tracker/types';
import { showError } from '@/tracker/utils/showError';

/** Участники очереди: список, выдача и отзыв доступа. Менять может только админ */
export class QueueAccessStore extends LocalStore {
  private readonly _queueKey: string;
  private readonly _alertsStore: AlertsStore;
  private readonly _members = new ValueModel<QueueMember[]>([]);
  private readonly _loadingStage = new LoadingStageModel();
  private readonly _savingStage = new LoadingStageModel();

  constructor({ queueKey, alertsStore }: { queueKey: string; alertsStore: AlertsStore }) {
    super();
    this._queueKey = queueKey;
    this._alertsStore = alertsStore;

    makeObservable(this, {
      members: computed,
      memberIds: computed,
      isLoading: computed,
      isError: computed,
      isSaving: computed,
      load: action.bound,
      add: action.bound,
      remove: action.bound,
    });
  }

  get members() {
    return this._members.value;
  }

  get memberIds() {
    return new Set(this._members.value.map((member) => member.user.id));
  }

  get isLoading() {
    return this._loadingStage.isLoading;
  }

  get isError() {
    return this._loadingStage.isError;
  }

  get isSaving() {
    return this._savingStage.isLoading;
  }

  async load() {
    try {
      this._loadingStage.loading();
      this._members.change(await trackerApi.listQueueMembers(this._queueKey));
      this._loadingStage.success();
    } catch (error) {
      this._loadingStage.error();
      showError({
        alertsStore: this._alertsStore,
        title: 'Не удалось загрузить участников очереди',
        error,
      });
    }
  }

  /** true — доступ выдан. Бывших участников бэкенд пропускает, новые — в конце: порядок по времени */
  async add(userIds: string[]): Promise<boolean> {
    try {
      this._savingStage.loading();

      const added = await trackerApi.addQueueMembers({ queueKey: this._queueKey, userIds });
      const known = this.memberIds;

      this._members.change([
        ...this._members.value,
        ...added.filter((member) => !known.has(member.user.id)),
      ]);
      this._savingStage.success();

      return true;
    } catch (error) {
      this._savingStage.error();
      showError({ alertsStore: this._alertsStore, title: 'Не удалось выдать доступ', error });

      return false;
    }
  }

  async remove(userId: string) {
    try {
      this._savingStage.loading();
      await trackerApi.removeQueueMembers({ queueKey: this._queueKey, userIds: [userId] });
      this._members.change(this._members.value.filter((member) => member.user.id !== userId));
      this._savingStage.success();
    } catch (error) {
      this._savingStage.error();
      showError({ alertsStore: this._alertsStore, title: 'Не удалось забрать доступ', error });
    }
  }
}
